import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import { PlusIcon, MinusIcon } from "@heroicons/react/24/outline"

import { useCreateNewAccountMutation } from "./accountsApiSlice"
import { useGetContactOrganizationsQuery } from "../contactOrganizations/contactOrganizationsApiSlice"
import { useGetOrganizationBUsQuery } from "../organizationBUs/organizationBUsApiSlice"

const EtCreateAccount = ({ setActiveAdminView, userOrgId = "" }) => {
  // First Created - 27 Apr 2026
  // Author - Atul Pandey
  // Purpose - To create an Account linked to a client ContactOrganization

  useTitle("Create New Account")

  const navigate = useNavigate()

  const [createNewAccount, { isLoading, isSuccess, isError, error }] =
    useCreateNewAccountMutation()

  const { contactOrganizations } = useGetContactOrganizationsQuery(
    "contactOrganizationsList",
    {
      selectFromResult: ({ data }) => ({
        contactOrganizations: data?.ids?.map(id => data?.entities[id]) || [],
      }),
    }
  )

  const { organizationBUs } = useGetOrganizationBUsQuery(userOrgId, {
    selectFromResult: ({ data }) => ({
      organizationBUs: data?.ids?.map(id => data?.entities[id]) || [],
    }),
  })

  const clientContactOrganizations = contactOrganizations?.filter(org => {
    const typeName =
      org?.contactOrganizationTypeId?.contactOrganizationTypeName ||
      org?.contactOrganizationTypeId?.name ||
      ""

    return typeName.toLowerCase() === "client"
  })

  const initialFormState = {
    ownerOrgId: userOrgId,
    contactOrganizationId: "",
    availableToOrganizationBUs: [],
    accountName: "",
    accountDesc: "",
    accountPurpose: "",
    accountIntro: "",
    accountOutro: "",
    accountStatus: "Prospect",
    accountStage: "",
    accountRevenueSize: "",
    accountProfitability: "",
    accountStartDate: "",
    accountOfferings: [{ offeringName: "", offeringDesc: "" }],
    accountCompetitors: [{ competitorName: "", competitorDesc: "" }],
    accountPartners: [{ partnerName: "", partnerDesc: "" }],
    accountRisks: [{ riskName: "", riskDesc: "" }],
    accountGrowthPriorities: [{ priorityName: "", priorityDesc: "" }],
    aboutMe: "",
    pic: "",
  }

  const [formData, setFormData] = useState(initialFormState)

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      ownerOrgId: userOrgId,
    }))
  }, [userOrgId])

  useEffect(() => {
    if (isSuccess) {
      setFormData(initialFormState)

      if (setActiveAdminView) {
        setActiveAdminView("accounts")
      } else {
        navigate("/accounts")
      }
    }
  }, [isSuccess])

  const handleChange = e => {
    const { name, value, files } = e.target

    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }))
    } else {
      setFormData(prevState => ({ ...prevState, [name]: value }))
    }
  }

  const handleContactOrganizationChange = e => {
    const selectedContactOrgId = e.target.value
    const selectedContactOrg = clientContactOrganizations?.find(
      org => org.id === selectedContactOrgId || org._id === selectedContactOrgId
    )

    setFormData(prev => ({
      ...prev,
      contactOrganizationId: selectedContactOrgId,
      accountName: selectedContactOrg?.contactOrganizationName || prev.accountName,
    }))
  }

  const handleBUSelection = e => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(option => ({
      organizationBUId: option.value,
    }))

    setFormData(prev => ({
      ...prev,
      availableToOrganizationBUs: selectedOptions,
    }))
  }

  const handleAdd = (fieldName, newItem) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: [...prevState[fieldName], newItem],
    }))
  }

  const handleRemove = (fieldName, index) => {
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: prevState[fieldName].filter((_, i) => i !== index),
    }))
  }

  const handleFieldChange = (fieldName, index, key, value) => {
    const updatedItems = formData[fieldName].map((item, i) =>
      i === index ? { ...item, [key]: value } : item
    )

    setFormData({
      ...formData,
      [fieldName]: updatedItems,
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (!userOrgId) {
      console.error("ownerOrgId is missing. Cannot create Account.")
      return
    }

    const accountData = {
      ownerOrgId: formData.ownerOrgId,
      contactOrganizationId: formData.contactOrganizationId,
      availableToOrganizationBUs: formData.availableToOrganizationBUs,
      accountName: formData.accountName,
      accountDesc: formData.accountDesc,
      accountPurpose: formData.accountPurpose,
      accountIntro: formData.accountIntro,
      accountOutro: formData.accountOutro,
      accountStatus: formData.accountStatus,
      accountStage: formData.accountStage,
      accountRevenueSize: formData.accountRevenueSize,
      accountProfitability: formData.accountProfitability,
      accountStartDate: formData.accountStartDate,
      accountOfferings: formData.accountOfferings,
      accountCompetitors: formData.accountCompetitors,
      accountPartners: formData.accountPartners,
      accountRisks: formData.accountRisks,
      accountGrowthPriorities: formData.accountGrowthPriorities,
      aboutMe: formData.aboutMe,
    }

    if (formData.pic) {
      const reader = new FileReader()

      reader.onloadend = async () => {
        accountData.pic = reader.result

        try {
          await createNewAccount(accountData).unwrap()
        } catch (error) {
          console.error(error)
        }
      }

      reader.readAsDataURL(formData.pic)
    } else {
      try {
        await createNewAccount(accountData).unwrap()
      } catch (error) {
        console.error(error)
      }
    }
  }

  const handleCancel = () => {
    if (setActiveAdminView) {
      setActiveAdminView("accounts")
    } else {
      navigate("/accounts")
    }
  }

  const handleClear = () => {
    setFormData(initialFormState)
  }

  const renderTwoFieldArray = ({
    title,
    subtitle,
    fieldName,
    firstKey,
    secondKey,
    firstPlaceholder,
    secondPlaceholder,
    addLabel,
  }) => (
    <div className="m-1 p-1 border border-white rounded">
      <div>
        <p className="font-bold mx-2 px-2 mt-2">{title}</p>
        <p className="text-[8px] mx-2 px-2 mb-2 italic text-gray-600">
          {subtitle}
        </p>
      </div>

      {formData[fieldName].map((item, index) => (
        <div key={index} className="grid grid-cols-12 gap-1 items-center mx-1">
          <input
            type="text"
            value={item[firstKey]}
            onChange={e =>
              handleFieldChange(fieldName, index, firstKey, e.target.value)
            }
            placeholder={firstPlaceholder}
            className="col-span-4 rounded p-2 m-1 text-xs"
          />

          <input
            type="text"
            value={item[secondKey]}
            onChange={e =>
              handleFieldChange(fieldName, index, secondKey, e.target.value)
            }
            placeholder={secondPlaceholder}
            className="col-span-7 rounded p-2 m-1 text-xs"
          />

          <div className="col-span-1 rounded bg-red-500 m-1 p-1 flex justify-center">
            <button type="button" onClick={() => handleRemove(fieldName, index)}>
              <MinusIcon className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      ))}

      <div className="flex justify-start items-center m-1">
        <p className="font-bold text-xs m-1 p-1">{addLabel || "Add"}</p>
        <button
          type="button"
          onClick={() =>
            handleAdd(fieldName, { [firstKey]: "", [secondKey]: "" })
          }
        >
          <PlusIcon className="h-5 w-5 text-white m-1 p-1 rounded bg-green-500" />
        </button>
      </div>
    </div>
  )

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}

      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">Add an Account</p>

        <div className="bg-gray-200 rounded m-1 p-1">
          <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
            <div id="contactOrganizationSelector">
              <select
                name="contactOrganizationId"
                value={formData.contactOrganizationId}
                onChange={handleContactOrganizationChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="">Select a Client Organization</option>
                {clientContactOrganizations?.map(contactOrg => (
                  <option key={contactOrg.id} value={contactOrg.id}>
                    {contactOrg.contactOrganizationName}
                  </option>
                ))}
              </select>
            </div>

            <div id="organizationBUSelector">
              <select
                multiple
                name="availableToOrganizationBUs"
                value={formData.availableToOrganizationBUs.map(
                  item => item.organizationBUId
                )}
                onChange={handleBUSelection}
                className="w-full rounded p-2 m-1 min-h-[90px]"
              >
                {organizationBUs?.map(bu => (
                  <option key={bu.id} value={bu.id}>
                    {bu.organizationBUName}
                    {bu.organizationBUCode ? ` (${bu.organizationBUCode})` : ""}
                  </option>
                ))}
              </select>
              <p className="text-[9px] text-gray-500 mx-2 px-2">
                Optional. Leave blank for a flat org level account.
              </p>
            </div>

            <div id="accountName">
              <input
                type="text"
                name="accountName"
                placeholder="Enter the account name here"
                value={formData.accountName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="accountDesc">
              <input
                type="text"
                name="accountDesc"
                placeholder="Enter the account description here"
                value={formData.accountDesc}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-1">
              <select
                name="accountStatus"
                value={formData.accountStatus}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="Prospect">Prospect</option>
                <option value="Active">Active</option>
                <option value="Strategic">Strategic</option>
                <option value="Dormant">Dormant</option>
                <option value="Inactive">Inactive</option>
              </select>

              <select
                name="accountStage"
                value={formData.accountStage}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              >
                <option value="">Select Account Stage</option>
                <option value="CreateAndNurture">Create And Nurture</option>
                <option value="MineAndGrab">Mine And Grab</option>
                <option value="ImmediateOpportunity">Immediate Opportunity</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-1">
              <input
                type="text"
                name="accountRevenueSize"
                placeholder="Revenue size"
                value={formData.accountRevenueSize}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

              <input
                type="text"
                name="accountProfitability"
                placeholder="Profitability"
                value={formData.accountProfitability}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />

              <input
                type="date"
                name="accountStartDate"
                value={formData.accountStartDate}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLightWithPlace
                value={formData.accountIntro}
                handleChange={content =>
                  setFormData({ ...formData, accountIntro: content })
                }
                placeholderText={"Write the opening account context here"}
              />
            </div>

            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLight3
                value={formData.accountOutro}
                handleChange={content =>
                  setFormData({ ...formData, accountOutro: content })
                }
                placeholderText={"Write the closing account context here"}
                toolbarId={"accountOutro"}
              />
            </div>

            <div id="aboutMe">
              <input
                type="text"
                name="aboutMe"
                placeholder="Add something about the personality of this account"
                value={formData.aboutMe}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            <div id="pic">
              <input
                type="file"
                name="pic"
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>

            {renderTwoFieldArray({
              title: "Offerings",
              subtitle: "What offerings are relevant for this account?",
              fieldName: "accountOfferings",
              firstKey: "offeringName",
              secondKey: "offeringDesc",
              firstPlaceholder: "Offering name",
              secondPlaceholder: "Offering description",
              addLabel: "Add Offering",
            })}

            {renderTwoFieldArray({
              title: "Competitors",
              subtitle: "Who are the key competitors in this account?",
              fieldName: "accountCompetitors",
              firstKey: "competitorName",
              secondKey: "competitorDesc",
              firstPlaceholder: "Competitor name",
              secondPlaceholder: "Competitor context",
              addLabel: "Add Competitor",
            })}

            {renderTwoFieldArray({
              title: "Partners",
              subtitle: "Who are the key partners around this account?",
              fieldName: "accountPartners",
              firstKey: "partnerName",
              secondKey: "partnerDesc",
              firstPlaceholder: "Partner name",
              secondPlaceholder: "Partner context",
              addLabel: "Add Partner",
            })}

            {renderTwoFieldArray({
              title: "Risks",
              subtitle: "What are the key account risks?",
              fieldName: "accountRisks",
              firstKey: "riskName",
              secondKey: "riskDesc",
              firstPlaceholder: "Risk name",
              secondPlaceholder: "Risk description",
              addLabel: "Add Risk",
            })}

            {renderTwoFieldArray({
              title: "Growth Priorities",
              subtitle: "What are the key growth priorities for this account?",
              fieldName: "accountGrowthPriorities",
              firstKey: "priorityName",
              secondKey: "priorityDesc",
              firstPlaceholder: "Priority name",
              secondPlaceholder: "Priority description",
              addLabel: "Add Priority",
            })}

            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Create"}
                type={"create"}
                handleClick={handleSubmit}
              />

              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />

              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EtCreateAccount